import React, { Component } from "react";
import { Input } from "reactstrap";
import {
  getCouponById,
  createCoupon,
  updateCoupon,
  getRoomTypeList,
  getRoomList,
  getFoodCuisineList,
  getFoodList,
  getTableList,
  getTransportServicesAdmin,
} from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import { C, Panel, Btn, Field, Toggle, Meter } from "../../components/Console/ConsoleUI";
import { canWrite } from "../../services/PermissionService";

/**
 * Coupon editor — screen E3·16 of the console redesign (web-coupon-editor.html).
 *
 * The handoff puts the whole coupon on one page beside a live guest-app card,
 * rather than behind a five-step wizard. That matters for the common job here,
 * which is editing an existing coupon: previously changing a discount value
 * meant paging through appearance and scope to reach Save.
 *
 * Every field, every piece of state and the entire request body below are the
 * ones the wizard used. The step validators are folded into one validateAll()
 * that runs the same checks in the same order with the same messages.
 */

const CSS = `
.coupon-editor .form-control,.coupon-editor .custom-select{height:48px;padding:0 15px;
  background:${C.surface};border:1px solid ${C.border};border-radius:12px;font-size:15px;
  color:${C.ink};box-shadow:none}
.coupon-editor .form-control:focus{background:#fff;border-color:${C.sand};box-shadow:none}
.coupon-editor .form-control::placeholder{color:${C.taupeLight}}
.coupon-editor textarea.form-control{height:auto;min-height:78px;padding:12px 15px;
  line-height:1.5;font-size:14px}
.coupon-chip{padding:9px 16px;border-radius:19px;font-size:14px;font-family:inherit;cursor:pointer;
  background:#fff;border:1px solid ${C.border};color:${C.taupe};line-height:1.3}
.coupon-chip.is-on{background:${C.brown};border-color:transparent;color:#fff}
.coupon-chip:disabled{cursor:default;opacity:.6}
.coupon-swatch{height:56px;border-radius:12px;cursor:pointer;display:grid;place-items:center;
  border:3px solid transparent}
.coupon-swatch.is-on{border-color:${C.brown}}
.coupon-rule{display:flex;align-items:center;gap:14px;padding:14px 0;
  border-bottom:1px solid ${C.panel}}
.coupon-rule:last-child{border-bottom:0}
`;

const money = (n) => `$${Number(n || 0).toLocaleString()}`;
const fmtDay = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;

/** Selectable pill — the "Applies to" row of the design. */
const SelChip = ({ on, onClick, children, disabled }) => (
  <button
    type="button"
    className={`coupon-chip${on ? " is-on" : ""}`}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
);

class CouponForm extends Component {
  constructor(props) {
    super(props);
    const couponId = this.props.match?.params?.id;

    // Theme gradient definitions
    this.themeGradients = {
      purple: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      sunset: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      ocean: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      forest: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
      fire: "linear-gradient(135deg, #f12711 0%, #f5af19 100%)",
      dark: "linear-gradient(135deg, #232526 0%, #414345 100%)",
      gold: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)",
      blue: "linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)",
    };

    this.state = {
      couponId: couponId || null,
      isEditMode: !!couponId,
      isLoading: couponId ? true : false,

      // Dropdown data
      roomTypes: [],
      rooms: [],
      cuisines: [],
      foods: [],
      tables: [],
      transportServices: [],

      // Basic Info
      code: "",
      name: "",
      description: "",

      // Appearance/Theme
      backgroundType: "gradient",
      backgroundTheme: "purple",
      backgroundImage: null,
      backgroundImageUrl: null,
      backgroundImageFile: null,
      backgroundImagePreview: null,
      textColor: "light",

      // Discount
      discountType: "percentage",
      discountValue: "",
      minOrderAmount: "",
      maxDiscount: "",

      // Where to Apply
      applyTo: "all", // all, specific
      selectedCategory: "", // food, room, table, transport (single selection)
      // Granular selections
      selectedRoomTypes: [],
      selectedRooms: [],
      selectedCuisines: [],
      selectedFoods: [],
      selectedTables: [],
      selectedTransportServices: [],
      selectedOrderPlaceTypes: [], // For food: 1=Room Service, 2=Dine In, 3=Pickup, 4=Home Delivery

      // Rules & Validity
      userType: "all",
      loyaltyMinOrders: 5,
      loyaltyMinSpending: 50000,
      usageLimit: "",
      usageLimitPerUser: 1,
      startDate: "",
      endDate: "",
      isActive: true,

      // Read-only facts about a saved coupon, taken from the same
      // getCouponById response the form already loads.
      currentUsage: 0,
      createdAt: null,
      updatedAt: null,
    };
  }

  componentDidMount() {
    this.loadDropdownData();
    if (this.state.couponId) {
      this.loadCoupon();
    }
  }

  loadDropdownData = async () => {
    try {
      const [roomTypesRes, roomsRes, cuisinesRes, foodsRes, tablesRes, transportRes] = await Promise.all([
        getRoomTypeList(),
        getRoomList(),
        getFoodCuisineList(),
        getFoodList(),
        getTableList(),
        getTransportServicesAdmin(),
      ]);

      this.setState({
        roomTypes: roomTypesRes?.data || [],
        rooms: roomsRes?.data || [],
        cuisines: cuisinesRes?.data || [],
        foods: foodsRes?.data || [],
        tables: tablesRes?.data || [],
        transportServices: transportRes?.data || [],
      });
    } catch (error) {
      console.error("Error loading dropdown data:", error);
    }
  };

  loadCoupon = () => {
    getCouponById(this.state.couponId)
      .then((result) => {
        if (result.status === 200) {
          const coupon = result.data;
          this.setState({
            isLoading: false,
            code: coupon.code || "",
            name: coupon.name || "",
            description: coupon.description || "",
            // Background/Theme
            backgroundType: coupon.backgroundType || "gradient",
            backgroundTheme: coupon.backgroundTheme || "purple",
            backgroundImageUrl: coupon.backgroundImageUrl || null,
            textColor: coupon.textColor || "light",
            // Discount
            discountType: coupon.discountType || "percentage",
            discountValue: coupon.discountValue || "",
            minOrderAmount: coupon.minOrderValue || "",
            maxDiscount: coupon.uptoAmount || "",
            applyTo: coupon.scopeLevel === "all" ? "all" : "specific",
            selectedCategory: coupon.applicableCategories?.[0] || "",
            selectedRoomTypes: coupon.applicableRoomTypes?.map(r => r._id || r) || [],
            selectedRooms: coupon.applicableRooms?.map(r => r._id || r) || [],
            selectedCuisines: coupon.applicableCuisines?.map(c => c._id || c) || [],
            selectedFoods: coupon.applicableFoodItems?.map(f => f._id || f) || [],
            selectedTables: coupon.applicableTables?.map(t => t._id || t) || [],
            selectedTransportServices: coupon.applicableTransportServices?.map(t => t._id || t) || [],
            selectedOrderPlaceTypes: coupon.applicableOrderPlaceTypes || [],
            userType: coupon.userType || "all",
            loyaltyMinOrders: coupon.loyaltyMinOrders || 5,
            loyaltyMinSpending: coupon.loyaltyMinSpending || 50000,
            usageLimit: coupon.usageLimit || "",
            usageLimitPerUser: coupon.usagePerUser || 1,
            startDate: coupon.startDate ? coupon.startDate.split("T")[0] : "",
            endDate: coupon.expiryDate ? coupon.expiryDate.split("T")[0] : "",
            isActive: coupon.isActive !== false,
            currentUsage: coupon.currentUsage || 0,
            createdAt: coupon.createdAt || null,
            updatedAt: coupon.updatedAt || null,
          });
        } else {
          toast.error(result.message || "Failed to load coupon");
          this.props.history.push("/coupon");
        }
      })
      .catch(() => {
        toast.error("Error loading coupon");
        this.props.history.push("/coupon");
      });
  };

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState({
      [name]: type === "checkbox" ? checked : value,
    });
  };

  generateCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    this.setState({ code });
  };

  handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload a valid image (JPG, PNG, or WebP)");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size must be less than 5MB");
        return;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);

      this.setState({
        backgroundType: "image",
        backgroundImageFile: file,
        backgroundImagePreview: previewUrl,
      });
    }
  };

  removeBackgroundImage = () => {
    // Revoke the preview URL to free memory
    if (this.state.backgroundImagePreview) {
      URL.revokeObjectURL(this.state.backgroundImagePreview);
    }
    this.setState({
      backgroundType: "gradient",
      backgroundImageFile: null,
      backgroundImagePreview: null,
      backgroundImageUrl: null, // Clear existing server image URL
      backgroundImage: null,
    });
  };

  selectCategory = (category) => {
    const { selectedCategory } = this.state;

    // If clicking the same category, deselect it
    if (selectedCategory === category) {
      this.setState({
        selectedCategory: "",
        // Clear all related selections
        selectedRoomTypes: [],
        selectedRooms: [],
        selectedCuisines: [],
        selectedFoods: [],
        selectedTables: [],
        selectedTransportServices: [],
        selectedOrderPlaceTypes: [],
      });
    } else {
      // Select new category and clear all previous selections
      this.setState({
        selectedCategory: category,
        selectedRoomTypes: [],
        selectedRooms: [],
        selectedCuisines: [],
        selectedFoods: [],
        selectedTables: [],
        selectedTransportServices: [],
        selectedOrderPlaceTypes: [],
      });
    }
  };

  toggleArrayItem = (field, itemId) => {
    const currentArray = this.state[field];
    if (currentArray.includes(itemId)) {
      this.setState({ [field]: currentArray.filter((id) => id !== itemId) });
    } else {
      this.setState({ [field]: [...currentArray, itemId] });
    }
  };

  /**
   * The wizard's four step validators, run together now that every field is on
   * screen at once. Same conditions, same messages, same order.
   */
  validateAll = () => {
    const { code, name, discountValue, applyTo, selectedCategory } = this.state;

    if (!code.trim()) {
      toast.error("Please enter a coupon code");
      return false;
    }
    if (!name.trim()) {
      toast.error("Please enter a coupon name");
      return false;
    }
    if (!discountValue || parseFloat(discountValue) <= 0) {
      toast.error("Please enter a valid discount value");
      return false;
    }
    if (applyTo === "specific" && !selectedCategory) {
      toast.error("Please select a category");
      return false;
    }
    return true;
  };

  handleSubmit = () => {
    if (!this.validateAll()) return;

    const {
      couponId,
      code,
      name,
      description,
      backgroundType,
      backgroundTheme,
      backgroundImageFile,
      textColor,
      discountType,
      discountValue,
      minOrderAmount,
      maxDiscount,
      applyTo,
      selectedCategory,
      selectedRoomTypes,
      selectedRooms,
      selectedCuisines,
      selectedFoods,
      selectedTables,
      selectedTransportServices,
      selectedOrderPlaceTypes,
      userType,
      loyaltyMinOrders,
      loyaltyMinSpending,
      usageLimit,
      usageLimitPerUser,
      startDate,
      endDate,
      isActive,
    } = this.state;

    // If there's an image file, use FormData
    if (backgroundImageFile) {
      const formData = new FormData();
      formData.append("couponImage", backgroundImageFile);
      formData.append("code", code.toUpperCase());
      formData.append("name", name);
      formData.append("description", description);
      formData.append("backgroundType", "image");
      formData.append("backgroundTheme", backgroundTheme);
      formData.append("textColor", textColor);
      formData.append("discountType", discountType);
      formData.append("discountValue", parseFloat(discountValue));
      formData.append("minOrderValue", minOrderAmount ? parseFloat(minOrderAmount) : 0);
      formData.append("uptoAmount", maxDiscount ? parseFloat(maxDiscount) : 0);
      formData.append("scopeLevel", applyTo === "all" ? "all" : "category");
      if (applyTo === "specific" && selectedCategory) {
        formData.append("applicableCategories[]", selectedCategory);
      }
      selectedRoomTypes.forEach(id => formData.append("applicableRoomTypes[]", id));
      selectedRooms.forEach(id => formData.append("applicableRooms[]", id));
      selectedCuisines.forEach(id => formData.append("applicableCuisines[]", id));
      selectedFoods.forEach(id => formData.append("applicableFoodItems[]", id));
      selectedTables.forEach(id => formData.append("applicableTables[]", id));
      selectedTransportServices.forEach(id => formData.append("applicableTransportServices[]", id));
      selectedOrderPlaceTypes.forEach(id => formData.append("applicableOrderPlaceTypes[]", id));
      formData.append("userType", userType);
      formData.append("loyaltyMinOrders", userType === "loyalty" ? parseInt(loyaltyMinOrders) : 5);
      formData.append("loyaltyMinSpending", userType === "loyalty" ? parseFloat(loyaltyMinSpending) : 50000);
      formData.append("usageLimit", usageLimit ? parseInt(usageLimit) : 0);
      // 0 means unlimited per user
      formData.append("usagePerUser", parseInt(usageLimitPerUser) >= 0 ? parseInt(usageLimitPerUser) : 1);
      formData.append("startDate", startDate || new Date().toISOString().split("T")[0]);
      formData.append("expiryDate", endDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]);
      formData.append("isActive", isActive);

      const apiCall = couponId ? updateCoupon(couponId, formData) : createCoupon(formData);
      this.executeApiCall(apiCall, couponId);
      return;
    }

    // No image - use JSON
    const couponData = {
      code: code.toUpperCase(),
      name,
      description,
      backgroundType,
      backgroundTheme,
      textColor,
      discountType,
      discountValue: parseFloat(discountValue),
      minOrderValue: minOrderAmount ? parseFloat(minOrderAmount) : 0,
      uptoAmount: maxDiscount ? parseFloat(maxDiscount) : 0,
      scopeLevel: applyTo === "all" ? "all" : "category",
      applicableCategories: applyTo === "specific" && selectedCategory ? [selectedCategory] : [],
      applicableRoomTypes: selectedRoomTypes,
      applicableRooms: selectedRooms,
      applicableCuisines: selectedCuisines,
      applicableFoodItems: selectedFoods,
      applicableTables: selectedTables,
      applicableTransportServices: selectedTransportServices,
      applicableOrderPlaceTypes: selectedOrderPlaceTypes,
      userType,
      loyaltyMinOrders: userType === "loyalty" ? parseInt(loyaltyMinOrders) : 5,
      loyaltyMinSpending: userType === "loyalty" ? parseFloat(loyaltyMinSpending) : 50000,
      usageLimit: usageLimit ? parseInt(usageLimit) : 0,
      usagePerUser: parseInt(usageLimitPerUser) >= 0 ? parseInt(usageLimitPerUser) : 1, // 0 means unlimited per user
      startDate: startDate || new Date().toISOString().split("T")[0],
      expiryDate: endDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      isActive,
    };

    const apiCall = couponId ? updateCoupon(couponId, couponData) : createCoupon(couponData);
    this.executeApiCall(apiCall, couponId);
  };

  executeApiCall = (apiCall, couponId) => {
    apiCall
      .then((response) => {
        if (response && response.status === 200) {
          toast.success(couponId ? "Coupon updated!" : "Coupon created!");
          setTimeout(() => this.props.history.push("/coupon"), 1500);
        } else {
          toast.error(response?.message || "Failed to save coupon");
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Error saving coupon");
      });
  };

  // Get display text for where coupon applies
  getAppliesTo = () => {
    const { applyTo, selectedCategory, selectedRoomTypes, selectedCuisines, selectedTables, selectedTransportServices, roomTypes, cuisines, transportServices } = this.state;

    if (applyTo === "all") return "All Services";

    if (!selectedCategory) return "Not selected";

    switch (selectedCategory) {
      case "food":
        if (selectedCuisines.length > 0) {
          const cuisineNames = cuisines.filter(c => selectedCuisines.includes(c._id)).map(c => c.cuisineName);
          return `Food (${cuisineNames.join(", ")})`;
        }
        return "All Food";

      case "room":
        if (selectedRoomTypes.length > 0) {
          const typeNames = roomTypes.filter(r => selectedRoomTypes.includes(r._id)).map(r => r.roomTypeName);
          return `Rooms (${typeNames.join(", ")})`;
        }
        return "All Rooms";

      case "table":
        return selectedTables.length > 0 ? `${selectedTables.length} Tables` : "All Tables";

      case "transport":
        if (selectedTransportServices.length > 0) {
          const serviceNames = transportServices.filter(s => selectedTransportServices.includes(s._id)).map(s => s.serviceName);
          return `Transport (${serviceNames.join(", ")})`;
        }
        return "All Transport";

      default:
        return "Not selected";
    }
  };

  /** The guest-app card from the design's right rail, honouring the chosen theme. */
  renderCouponPreview() {
    const {
      code, name, description, backgroundType, backgroundTheme,
      backgroundImagePreview, backgroundImageUrl, textColor,
      discountType, discountValue, minOrderAmount, maxDiscount, endDate, isActive,
    } = this.state;

    const discountDisplay = discountValue
      ? discountType === "percentage"
        ? `${discountValue}% OFF`
        : `${money(discountValue)} OFF`
      : "-- OFF";

    // For server images, prepend API base URL
    const serverImageUrl = backgroundImageUrl && !backgroundImageUrl.startsWith("blob:") && !backgroundImageUrl.startsWith("http")
      ? `${process.env.REACT_APP_DEFAULTAUTH}${backgroundImageUrl}`
      : backgroundImageUrl;
    const imageUrl = backgroundImagePreview || serverImageUrl;

    let backgroundStyle;
    if (!isActive) {
      backgroundStyle = { background: "linear-gradient(135deg, #6c757d 0%, #495057 100%)" };
    } else if (backgroundType === "image" && imageUrl) {
      backgroundStyle = {
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    } else {
      backgroundStyle = { background: this.themeGradients[backgroundTheme] || this.themeGradients.purple };
    }

    const ink = textColor === "dark" ? "#2b2018" : "#ffffff";
    const dim = textColor === "dark" ? "rgba(43,32,24,.62)" : "rgba(255,255,255,.66)";

    const meta = [
      minOrderAmount ? `Min. ${money(minOrderAmount)}` : null,
      discountType === "percentage" && maxDiscount ? `max ${money(maxDiscount)} off` : null,
      endDate ? `ends ${fmtDay(endDate)}` : "no expiry set",
    ].filter(Boolean).join(" · ");

    return (
      <div style={{ borderRadius: 18, padding: 24, position: "relative", overflow: "hidden", color: ink, ...backgroundStyle }}>
        {backgroundType === "image" && imageUrl && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.42)" }} />
        )}
        <div style={{ position: "relative" }}>
          <div style={{ fontSize: 13, color: dim, letterSpacing: 1 }}>GUEST APP CARD</div>
          <div style={{ marginTop: 14, fontSize: 40, fontWeight: 500, letterSpacing: -1, lineHeight: 1 }}>
            {discountDisplay}
          </div>
          <div style={{ marginTop: 8, fontSize: 15, color: dim }}>
            {description || name || "Name this coupon"}
          </div>
          <div
            style={{
              marginTop: 18, padding: 12, borderRadius: 12,
              border: `1px dashed ${textColor === "dark" ? "rgba(43,32,24,.35)" : "rgba(255,255,255,.4)"}`,
              textAlign: "center", fontSize: 18, fontWeight: 700, letterSpacing: 3,
              wordBreak: "break-all",
            }}
          >
            {(code || "CODE").toUpperCase()}
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: dim }}>{meta}</div>
          {!isActive && (
            <div style={{ marginTop: 12, fontSize: 12, color: dim }}>
              Inactive — guests cannot see this card.
            </div>
          )}
        </div>
      </div>
    );
  }

  renderBasics() {
    const { code, name, description, discountType, discountValue, minOrderAmount, maxDiscount } = this.state;
    const write = canWrite();

    return (
      <Panel title="Coupon basics">
        <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Field label="Code" hint="Letters and numbers only, no spaces">
            <div style={{ display: "flex", gap: 8 }}>
              <Input
                type="text"
                name="code"
                value={code}
                onChange={this.handleChange}
                placeholder="e.g. SUMMER20"
                style={{ fontFamily: "monospace", letterSpacing: 2, textTransform: "uppercase" }}
              />
              {write && (
                <Btn kind="quiet" icon="autorenew" onClick={this.generateCode} style={{ height: 48, flex: "0 0 auto" }}>
                  Generate
                </Btn>
              )}
            </div>
          </Field>

          <Field label="Internal name" hint="How your team will recognise it">
            <Input
              type="text"
              name="name"
              value={name}
              onChange={this.handleChange}
              placeholder="e.g. Summer Sale - 20% Off"
            />
          </Field>

          <Field label="Discount type">
            <Input type="select" name="discountType" value={discountType} onChange={this.handleChange}>
              <option value="percentage">Percentage off</option>
              <option value="fixed">Fixed amount off</option>
            </Input>
          </Field>

          <Field label={discountType === "percentage" ? "Value (%)" : "Value ($)"}>
            <Input
              type="number"
              name="discountValue"
              value={discountValue}
              onChange={this.handleChange}
              placeholder={discountType === "percentage" ? "e.g. 20" : "e.g. 500"}
              min="0"
              max={discountType === "percentage" ? "100" : undefined}
            />
          </Field>

          <Field label="Min order ($)" hint="Leave empty for no minimum">
            <Input
              type="number"
              name="minOrderAmount"
              value={minOrderAmount}
              onChange={this.handleChange}
              placeholder="e.g. 100"
              min="0"
            />
          </Field>

          {discountType === "percentage" ? (
            <Field label="Max discount ($)" hint="Leave empty for no cap">
              <Input
                type="number"
                name="maxDiscount"
                value={maxDiscount}
                onChange={this.handleChange}
                placeholder="e.g. 500"
                min="0"
              />
            </Field>
          ) : (
            <div />
          )}

          <div style={{ gridColumn: "1 / -1" }}>
            <Field label="Description" hint="Shown to guests on the coupon card">
              <Input
                type="textarea"
                name="description"
                value={description}
                onChange={this.handleChange}
                rows={2}
                placeholder="Brief description for customers…"
              />
            </Field>
          </div>
        </div>
      </Panel>
    );
  }

  renderAppearance() {
    const { backgroundType, backgroundTheme, backgroundImagePreview, backgroundImageUrl, textColor } = this.state;
    const write = canWrite();

    // For existing images from server, prepend API base URL
    const serverImageUrl = backgroundImageUrl && !backgroundImageUrl.startsWith("blob:")
      ? `${process.env.REACT_APP_DEFAULTAUTH}${backgroundImageUrl}`
      : null;
    const imagePreview = backgroundImagePreview || serverImageUrl;

    const themes = [
      { id: "purple", name: "Purple" }, { id: "sunset", name: "Sunset" },
      { id: "ocean", name: "Ocean" }, { id: "forest", name: "Forest" },
      { id: "fire", name: "Fire" }, { id: "dark", name: "Dark" },
      { id: "gold", name: "Gold" }, { id: "blue", name: "Blue" },
    ];

    return (
      <Panel title="Appearance" subtitle="How the card looks in the guest app">
        <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
          <SelChip on={backgroundType === "gradient"} onClick={() => this.setState({ backgroundType: "gradient" })}>
            Theme colour
          </SelChip>
          <SelChip on={backgroundType === "image"} onClick={() => this.setState({ backgroundType: "image" })}>
            Custom image
          </SelChip>
          <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
            <SelChip on={textColor === "light"} onClick={() => this.setState({ textColor: "light" })}>
              Light text
            </SelChip>
            <SelChip on={textColor === "dark"} onClick={() => this.setState({ textColor: "dark" })}>
              Dark text
            </SelChip>
          </div>
        </div>

        {backgroundType === "gradient" && (
          <div
            style={{
              marginTop: 18, display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 12,
            }}
          >
            {themes.map((t) => (
              <div key={t.id}>
                <div
                  className={`coupon-swatch${backgroundTheme === t.id ? " is-on" : ""}`}
                  style={{ background: this.themeGradients[t.id] }}
                  onClick={() => this.setState({ backgroundTheme: t.id })}
                  title={t.name}
                >
                  {backgroundTheme === t.id && (
                    <span className="ms" style={{ fontSize: 22, color: "#fff" }}>check_circle</span>
                  )}
                </div>
                <div style={{ marginTop: 6, textAlign: "center", fontSize: 12, color: C.taupeLight }}>
                  {t.name}
                </div>
              </div>
            ))}
          </div>
        )}

        {backgroundType === "image" && (
          <div style={{ marginTop: 18 }}>
            <input
              type="file"
              id="couponImageInput"
              style={{ display: "none" }}
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={this.handleImageUpload}
            />
            {imagePreview ? (
              <div>
                <div style={{ position: "relative", borderRadius: 14, overflow: "hidden" }}>
                  <img src={imagePreview} alt="Coupon background" style={{ width: "100%", height: 140, objectFit: "cover", display: "block" }} />
                  {write && (
                    <button
                      type="button"
                      onClick={this.removeBackgroundImage}
                      title="Remove image"
                      style={{
                        position: "absolute", top: 10, right: 10, width: 30, height: 30,
                        borderRadius: 15, border: 0, background: "rgba(255,255,255,.92)",
                        color: C.red, cursor: "pointer", display: "grid", placeItems: "center", padding: 0,
                      }}
                    >
                      <span className="ms" style={{ fontSize: 17 }}>close</span>
                    </button>
                  )}
                </div>
                {write && (
                  <div style={{ marginTop: 12 }}>
                    <Btn kind="quiet" icon="autorenew" onClick={() => document.getElementById("couponImageInput").click()}>
                      Replace image
                    </Btn>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                disabled={!write}
                onClick={() => document.getElementById("couponImageInput").click()}
                style={{
                  width: "100%", padding: "30px 20px", borderRadius: 14,
                  border: "1.5px dashed #dfcdb2", background: C.surface,
                  color: C.taupeLight, cursor: write ? "pointer" : "default",
                  fontFamily: "inherit", fontSize: 14,
                }}
              >
                <span className="ms" style={{ fontSize: 28, display: "block", marginBottom: 8 }}>
                  add_photo_alternate
                </span>
                Upload a background image
                <div style={{ marginTop: 4, fontSize: 12 }}>JPG, PNG or WebP · max 5 MB</div>
              </button>
            )}
          </div>
        )}
      </Panel>
    );
  }

  renderAppliesTo() {
    const {
      applyTo, selectedCategory, selectedRoomTypes, selectedRooms, selectedCuisines,
      selectedFoods, selectedTables, selectedTransportServices, selectedOrderPlaceTypes,
      roomTypes, rooms, cuisines, foods, tables, transportServices,
      startDate, endDate, usageLimit, usageLimitPerUser,
    } = this.state;

    const wrap = { display: "flex", gap: 10, flexWrap: "wrap" };
    const sub = (label, hint, children) => (
      <div style={{ marginTop: 18 }}>
        <div style={{ fontSize: 12, letterSpacing: ".6px", textTransform: "uppercase", color: C.taupe, marginBottom: 9 }}>
          {label}
        </div>
        <div style={wrap}>{children}</div>
        {hint && <div style={{ marginTop: 8, fontSize: 12, color: C.taupeLight }}>{hint}</div>}
      </div>
    );

    return (
      <Panel title="Applies to">
        <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <SelChip
            on={applyTo === "all"}
            onClick={() =>
              this.setState({
                applyTo: "all", selectedCategory: "", selectedRoomTypes: [], selectedRooms: [],
                selectedCuisines: [], selectedFoods: [], selectedTables: [],
                selectedTransportServices: [], selectedOrderPlaceTypes: [],
              })
            }
          >
            All services
          </SelChip>
          {[
            { id: "food", label: "Food orders" },
            { id: "room", label: "Room booking" },
            { id: "table", label: "Table booking" },
            { id: "transport", label: "Transport" },
          ].map((cat) => (
            <SelChip
              key={cat.id}
              on={applyTo === "specific" && selectedCategory === cat.id}
              onClick={() => {
                if (applyTo !== "specific") this.setState({ applyTo: "specific" });
                this.selectCategory(cat.id);
              }}
            >
              {cat.label}
            </SelChip>
          ))}
        </div>

        {applyTo === "all" && (
          <div style={{ marginTop: 16, fontSize: 13, color: C.taupeLight }}>
            Redeemable against food, rooms, tables and transport.
          </div>
        )}

        {applyTo === "specific" && !selectedCategory && (
          <div style={{ marginTop: 16, fontSize: 13, color: C.red }}>
            Pick one service above, or go back to “All services”.
          </div>
        )}

        {applyTo === "specific" && selectedCategory === "food" && (
          <React.Fragment>
            {sub(
              "Cuisines",
              "Leave empty to apply to all cuisines",
              cuisines.map((cuisine) => (
                <SelChip
                  key={cuisine._id}
                  on={selectedCuisines.includes(cuisine._id)}
                  onClick={() => this.toggleArrayItem("selectedCuisines", cuisine._id)}
                >
                  {cuisine.cuisineName}
                </SelChip>
              ))
            )}
            {selectedCuisines.length > 0 &&
              sub(
                "Specific dishes",
                "Leave empty to apply to every dish in those cuisines",
                foods
                  .filter((f) => selectedCuisines.includes(f.foodCuisine?._id || f.foodCuisine))
                  .map((food) => (
                    <SelChip
                      key={food._id}
                      on={selectedFoods.includes(food._id)}
                      onClick={() => this.toggleArrayItem("selectedFoods", food._id)}
                    >
                      {food.foodName}
                    </SelChip>
                  ))
              )}
            {sub(
              "Order type",
              "Leave empty to apply to all order types",
              [
                { id: 1, label: "Room Service" },
                { id: 2, label: "Dine In" },
                { id: 3, label: "Pickup" },
                { id: 4, label: "Home Delivery" },
              ].map((opt) => (
                <SelChip
                  key={opt.id}
                  on={selectedOrderPlaceTypes.includes(opt.id)}
                  onClick={() => this.toggleArrayItem("selectedOrderPlaceTypes", opt.id)}
                >
                  {opt.label}
                </SelChip>
              ))
            )}
          </React.Fragment>
        )}

        {applyTo === "specific" && selectedCategory === "room" && (
          <React.Fragment>
            {sub(
              "Room types",
              "Leave empty to apply to all room types",
              roomTypes.map((type) => (
                <SelChip
                  key={type._id}
                  on={selectedRoomTypes.includes(type._id)}
                  onClick={() => this.toggleArrayItem("selectedRoomTypes", type._id)}
                >
                  {type.roomTypeName}
                </SelChip>
              ))
            )}
            {selectedRoomTypes.length > 0 &&
              sub(
                "Specific rooms",
                "Leave empty to apply to every room of those types",
                rooms
                  .filter((r) => selectedRoomTypes.includes(r.roomType?._id || r.roomType))
                  .map((room) => (
                    <SelChip
                      key={room._id}
                      on={selectedRooms.includes(room._id)}
                      onClick={() => this.toggleArrayItem("selectedRooms", room._id)}
                    >
                      {room.roomName}
                    </SelChip>
                  ))
              )}
          </React.Fragment>
        )}

        {applyTo === "specific" && selectedCategory === "table" &&
          sub(
            "Tables",
            "Leave empty to apply to all tables",
            tables.map((table) => (
              <SelChip
                key={table._id}
                on={selectedTables.includes(table._id)}
                onClick={() => this.toggleArrayItem("selectedTables", table._id)}
              >
                Table {table.tableNo} ({table.seats} seats)
              </SelChip>
            ))
          )}

        {applyTo === "specific" && selectedCategory === "transport" && (
          <React.Fragment>
            {sub(
              "Transport services",
              "Leave empty to apply to all transport services",
              transportServices.map((service) => (
                <SelChip
                  key={service._id}
                  on={selectedTransportServices.includes(service._id)}
                  onClick={() => this.toggleArrayItem("selectedTransportServices", service._id)}
                >
                  {service.serviceName}
                </SelChip>
              ))
            )}
            {transportServices.length === 0 && (
              <div style={{ marginTop: 14, fontSize: 13, color: C.taupeLight }}>
                No transport services found. Run the seed script first.
              </div>
            )}
          </React.Fragment>
        )}

        <div
          style={{
            marginTop: 22, paddingTop: 20, borderTop: `1px solid ${C.panel}`,
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16,
          }}
        >
          <Field label="Starts" hint="Empty = today">
            <Input type="date" name="startDate" value={startDate} onChange={this.handleChange} />
          </Field>
          <Field label="Ends" hint="Empty = one year out">
            <Input type="date" name="endDate" value={endDate} onChange={this.handleChange} />
          </Field>
          <Field label="Total uses" hint="Empty = unlimited">
            <Input
              type="number"
              name="usageLimit"
              value={usageLimit}
              onChange={this.handleChange}
              placeholder="Unlimited"
              min="1"
            />
          </Field>
          <Field label="Uses per guest">
            <Input
              type="number"
              name="usageLimitPerUser"
              value={usageLimitPerUser}
              onChange={this.handleChange}
              min="1"
            />
          </Field>
        </div>
      </Panel>
    );
  }

  renderRules() {
    const { userType, loyaltyMinOrders, loyaltyMinSpending, isActive } = this.state;

    const rule = (key, title, blurbText) => (
      <div key={key} className="coupon-rule">
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, color: C.ink }}>{title}</div>
          <div style={{ marginTop: 2, fontSize: 13, color: C.taupeLight }}>{blurbText}</div>
        </div>
        <Toggle
          on={userType === key}
          title={title}
          onClick={() => this.setState({ userType: userType === key ? "all" : key })}
        />
      </div>
    );

    return (
      <Panel title="Rules">
        <div style={{ marginTop: 6 }}>
          {rule("first_time", "First order only", "Guest must never have completed an order")}
          {rule("loyalty", "Loyal guests only", "Guest must meet either loyalty threshold below")}

          {userType === "loyalty" && (
            <div
              style={{
                margin: "6px 0 14px", padding: 16, borderRadius: 14,
                background: C.surface, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16,
              }}
            >
              <Field label="Minimum orders" hint="Completed orders">
                <Input
                  type="number"
                  name="loyaltyMinOrders"
                  value={loyaltyMinOrders}
                  onChange={this.handleChange}
                  min="1"
                />
              </Field>
              <Field label="Minimum spend ($)" hint="Lifetime spend">
                <Input
                  type="number"
                  name="loyaltyMinSpending"
                  value={loyaltyMinSpending}
                  onChange={this.handleChange}
                  min="0"
                />
              </Field>
              <div style={{ gridColumn: "1 / -1", fontSize: 12, color: C.taupeLight }}>
                A guest qualifies by meeting <strong>either</strong> threshold.
              </div>
            </div>
          )}

          <div className="coupon-rule">
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, color: C.ink }}>Live in the guest app</div>
              <div style={{ marginTop: 2, fontSize: 13, color: C.taupeLight }}>
                {isActive
                  ? "Guests can find and redeem this code."
                  : "Hidden from guests until you switch this on."}
              </div>
            </div>
            <Toggle
              on={isActive}
              title="Active"
              onClick={() => this.setState({ isActive: !isActive })}
            />
          </div>

          {userType === "all" && (
            <div style={{ paddingTop: 14, fontSize: 13, color: C.taupeLight }}>
              With both guest rules off, any guest can redeem this code.
            </div>
          )}
        </div>
      </Panel>
    );
  }

  render() {
    const { isEditMode, isLoading, code, currentUsage, usageLimit, usageLimitPerUser, createdAt, updatedAt } = this.state;
    const write = canWrite();

    if (isLoading) {
      return (
        <ConsolePage title="Edit coupon" subtitle="Marketing / Coupons">
          <div style={{ padding: "80px 0", textAlign: "center", color: C.taupeLight }}>
            Loading coupon…
          </div>
        </ConsolePage>
      );
    }

    return (
      <ConsolePage
        title={isEditMode ? `Edit coupon · ${code || ""}`.trim() : "New coupon"}
        subtitle="Marketing / Coupons"
        actions={
          <React.Fragment>
            <Btn kind="quiet" icon="arrow_back" onClick={() => this.props.history.push("/coupon")}>
              Back to campaigns
            </Btn>
            {write && (
              <Btn icon="check" onClick={this.handleSubmit}>
                {isEditMode ? "Save coupon" : "Create coupon"}
              </Btn>
            )}
          </React.Fragment>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />

        <div className="coupon-editor" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 380px", gap: 20, alignItems: "start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
            {this.renderBasics()}
            {this.renderAppearance()}
            {this.renderAppliesTo()}
            {this.renderRules()}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 18, position: "sticky", top: 100 }}>
            {this.renderCouponPreview()}

            <Panel title="Where it applies" style={{ padding: 20 }}>
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 11, fontSize: 14, color: C.slate }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <span>Scope</span>
                  <span style={{ color: C.ink, textAlign: "right" }}>{this.getAppliesTo()}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <span>Who can use</span>
                  <span style={{ color: C.ink, textAlign: "right" }}>
                    {this.state.userType === "all" && "Any guest"}
                    {this.state.userType === "first_time" && "First-time guests"}
                    {this.state.userType === "loyalty" && `Loyal guests (${this.state.loyaltyMinOrders}+ orders)`}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <span>Uses per guest</span>
                  <span style={{ color: C.ink }}>
                    {parseInt(usageLimitPerUser, 10) === 0 ? "Unlimited" : usageLimitPerUser}
                  </span>
                </div>
              </div>
            </Panel>

            {isEditMode && (
              <Panel title="Live performance" style={{ padding: 20 }}>
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>
                  <Meter
                    label="Redemptions"
                    value={currentUsage}
                    max={usageLimit ? parseInt(usageLimit, 10) : 0}
                    note={
                      usageLimit
                        ? null
                        : "No total cap set, so there is no target to measure against."
                    }
                  />
                  <div style={{ fontSize: 12, color: C.taupeLight }}>
                    The coupon API reports redemption counts only — discount given and
                    revenue driven are not available, so they are not shown.
                  </div>
                </div>
              </Panel>
            )}

            {isEditMode && (createdAt || updatedAt) && (
              <Panel title="History" style={{ padding: 20 }}>
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12, fontSize: 13, color: C.taupe }}>
                  {updatedAt && (
                    <div>
                      <span style={{ color: C.ink }}>Last updated</span>
                      <br />
                      {fmtDay(updatedAt)}
                    </div>
                  )}
                  {createdAt && (
                    <div>
                      <span style={{ color: C.ink }}>Coupon created</span>
                      <br />
                      {fmtDay(createdAt)}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: C.taupeLight }}>
                    Per-change history with authors is not recorded by the API.
                  </div>
                </div>
              </Panel>
            )}
          </div>
        </div>
      </ConsolePage>
    );
  }
}

export default CouponForm;
